import { ChangeDetectionStrategy, Component, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { filter, Subscription } from 'rxjs';
import { Menu } from './shared/components/menu/models/menu';
import { PermissionService } from './shared/services/permission/permission.service';
import { PermissionEnum, ResourceEnum } from '../environments/shared-environment/permission';
import { PageKey } from './shared/services/page/models/page.key';
import { ProgressComponent } from './shared/components/export/progress.component';
import { ThemeService } from './shared/services/theme/theme.service';
import { MeService } from './shared/services/me/me.service';
import { ToastModule } from 'primeng/toast';
import { RibbonComponent } from './shared/components/ribbon/ribbon.component';
import { ParamsService } from './shared/services/params/params.service';
import { ButtonModule } from 'primeng/button';
import { ToastComponent } from './shared/components/toast/toast.component';
import { GlobalCacheConfig } from 'ts-cacheable';
import { PrimeNG } from 'primeng/config';
import { Liga } from "../themes/presets/liga";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LayoutComponent, HeaderComponent, ProgressComponent, ToastModule, RibbonComponent, ButtonModule, ToastComponent],
  template: `
    <l-layout [fullscreen]="fullscreen()">
      <l-header class="block w-full h-auto" header [menus]="menus()" />
      <router-outlet />
    </l-layout>

    <l-toast />
    <l-ribbon />
    <l-progress />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {

  constructor(
    router: Router,
    params: ParamsService,
    route: ActivatedRoute,
    themeService: ThemeService,
    private readonly primeng: PrimeNG,
    private readonly meService: MeService,
    private readonly permissionService: PermissionService,
  ) {
    params.initialize();
    themeService.initialize();

    GlobalCacheConfig.maxAge = 3600000;
    GlobalCacheConfig.maxCacheCount = 100;

    this.primeng.setTranslation({
      firstDayOfWeek: 0,
      dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
      dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sá'],
      monthNames: [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ],
      monthNamesShort: [
        'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
        'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
      ],
      today: 'Hoje',
      clear: 'Limpar',
      dateFormat: 'dd/mm/yy',
      weekHeader: 'Sem'
    });

    this.primeng.theme.set({
      preset: Liga,
      options: {
        darkModeSelector: 'dark',
        cssLayer: {
          name: 'primeng',
          order: 'reset, base, primeng, utilities'
        },
        /*  ripple: true,
          mergeSections: true*/
      }
    });

    this.routerSubscription = router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        let currentRoute = route.root.children[0].snapshot.data;
        this.fullscreen.set(currentRoute['fullscreen'] || false);
      });

    effect(() => {
      meService.me();
      this.checkMenu();
    });
  }

  public readonly fullscreen = signal(false);

  public readonly menus = signal<Menu[]>([]);

  private readonly routerSubscription?: Subscription;

  public async ngOnInit(): Promise<void> {
    await this.meService.storageMe();
  }

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  private checkMenu(): void {
    const can = (resource: ResourceEnum, permission: PermissionEnum, menu: Menu | Menu[]): Menu[] => this.permissionService.has(resource, permission) ? (Array.isArray(menu) ? menu : [menu]) : [];

    this.menus.set([
      {
        icon: 'home',
        title: 'Visão Geral',
        subtitle: 'Página inicial',
        link: '/home',
        key: PageKey.HOME,
      },
      {
        icon: 'home',
        title: 'Usuários',
        subtitle: 'Usuários',
        link: '/users',
        key: PageKey.USERS,
      },
      {
        icon: 'home',
        title: 'Ocorrências',
        subtitle: 'Ocorrências',
        link: '/occurrences',
        key: PageKey.OCCURRENCES,
      },
      {
        icon: 'home',
        title: 'Setores',
        subtitle: 'Setores',
        link: '/sectors',
        key: PageKey.SECTORS,
      },
      {
        icon: 'home',
        title: 'Ativos',
        subtitle: 'Ativos',
        link: '/assets',
        key: PageKey.ASSETS,
      },
      {
        icon: 'home',
        title: 'Tarefas',
        subtitle: 'Tarefas',
        link: '/tasks',
        key: PageKey.TASKS,
      },
      {
        icon: 'home',
        title: 'Eventos',
        subtitle: 'Eventos',
        link: '/events',
        key: PageKey.EVENTS,
      },
      {
        icon: 'home',
        title: 'Locais',
        subtitle: 'Locais',
        link: '/places',
        key: PageKey.PLACES,
      },
      {
        icon: 'home',
        title: 'Checklists',
        subtitle: 'Templates de Checklist',
        link: '/checklists',
        key: PageKey.CHECKLISTS,
      },
      // ...can(ResourceEnum.USERS, PermissionEnum.VIEW, {
      //   icon: 'group',
      //   title: 'Usuários',
      //   subtitle: 'Gerencie usuários',
      //   link: '/users',
      //   key: PageKey.USERS,
      // }),
    ]);
  }

}
